import { comboIndex } from '../range/combos';
import { startingRange } from '../range/starting';
import { deriveHeroPreflopSpot } from './heroSpot';
import { recommendPreflop } from './preflop';
import { positionBucket } from './villainRange';
const EPS = 1e-9;
/** Postflop acting order BY POSITION (SB acts first, BTN last) - the IP/OOP test without importing
 *  hand/positions.ts (`postflopOrder` lives in hand/, and strategy/ does not depend on hand/). */
const POSTFLOP_RANK = ['SB', 'BB', 'UTG', 'UTG1', 'UTG2', 'LJ', 'HJ', 'CO', 'BTN'];
/** The three shipped table-type families, tightest → loosest. Mirrors preflop.ts's WIDTH_ORDER; note (as
 *  that module documents) the ordering holds for RFI/_call charts, NOT for 3bet charts. */
const WIDTH_ORDER = ['live_tight', 'baseline', 'live_loose'];
/** A villain's rule-matching context is inert here (rules are empty by design - see the header), so a
 *  neutral shape is the honest placeholder rather than a fabricated read. */
const NEUTRAL_SHAPE = { valueHeavyPct: 50, bluffPct: 10, topClasses: [] };
/** `'live_loose_9max'` → `'live_loose'`. The archetype's `preflop.rangeSetId` names the family it plays. */
export const familyOfRangeSetId = (id) => {
    for (const f of WIDTH_ORDER)
        if (id.startsWith(f))
            return f;
    return 'baseline';
};
const shiftLooser = (t) => WIDTH_ORDER[Math.min(WIDTH_ORDER.indexOf(t) + 1, WIDTH_ORDER.length - 1)];
const massOf = (r) => r.weights.reduce((a, b) => a + b, 0);
const inRange = (r, cards) => r.weights[comboIndex(cards[0], cards[1])] > EPS;
/**
 * Convert a frequency knob into a per-combo promote probability, calibrated against the SHIPPED charts.
 *
 * A knob is a multiple of the chart's own frequency: `scale: 1.7` = "plays 1.7× the combos its chart
 * plays". The extra combos have to come from somewhere real - the `widen` band, itself a shipped chart.
 * Solving `base + p·widen = scale·base` for p gives the promote probability below, so the REALIZED
 * frequency lands on the knob by construction rather than by tuning. Clamped to [0,1]: a knob asking for
 * more combos than the widen band holds simply takes the whole band (it can never invent one).
 */
const promoteProb = (scale, baseMass, widenMass) => {
    if (scale <= 1 || baseMass <= 0 || widenMass <= EPS)
        return 0;
    return Math.min(1, ((scale - 1) * baseMass) / widenMass);
};
/** Chart lookup for (family, key) at this table size - undefined when the family or key isn't shipped. */
const chart = (deps, t, key) => {
    const set = deps.rangeSetOf(`${t}_${deps.tableSize}max`);
    if (!set || set.ranges[key] === undefined)
        return undefined;
    return startingRange(set, key);
};
/** The band a vpip-widen draws extra combos from, for an OPEN node.
 *
 *  Precedent: preflop.ts's `applyPreflopBiases` widens an open by "consulting the LOOSER family's RFI".
 *  We reuse that verbatim. When there IS no looser family - the archetype already plays live_loose, which
 *  is the case for every genuine live whale - we fall back to the widest RFI shipped in its OWN family
 *  (`BTN_RFI`, present in every set). That is the documented "hands this player type will voluntarily put
 *  money in with from any seat" proxy, the same shape of shipped-artifact proxy villainRange.ts already
 *  uses for limped pots. Returns undefined when no band exists (e.g. a live_loose villain on the BTN,
 *  where base and band are the same chart - a 56% BTN range needs no widening). */
const openWidenBand = (deps, family, position) => {
    const looser = shiftLooser(family);
    if (looser !== family) {
        const c = chart(deps, looser, `${position}_RFI`);
        if (c)
            return { band: c, source: `${looser}:${position}_RFI` };
    }
    if (position === 'BTN')
        return undefined; // own-family BTN_RFI IS the base chart here.
    const c = chart(deps, family, 'BTN_RFI');
    return c ? { band: c, source: `${family}:BTN_RFI` } : undefined;
};
/** The band a vpip-widen draws extra flats from, for a DEFEND node: the looser family's `_call` chart.
 *  A live_loose villain gets none - its call charts are already the widest shipped (BB_vs_CO_call is 61%
 *  of all hands), so there is nothing honest left to widen INTO. */
const defendWidenBand = (deps, family, key) => {
    const looser = shiftLooser(family);
    return looser === family ? undefined : chart(deps, looser, key);
};
/** Build the PreflopContext recommendPreflop() answers FOR THE VILLAIN. `villain` on the context is the
 *  OPPONENT the exploit rules would read; with `rules: []` nothing matches, so the archetype's own profile
 *  is inert provenance here rather than a claim that the bot reads itself. */
const villainCtx = (state, seat, arch, cards, deps, rules) => {
    const { heroPosition: position, facing } = deriveHeroPreflopSpot(state, seat);
    const bb = state.spec.stakes.bb;
    const pot = state.pots.reduce((s, p) => s + p.amount, 0);
    const family = familyOfRangeSetId(arch.preflop.rangeSetId);
    const families = {};
    for (const t of WIDTH_ORDER) {
        const set = deps.rangeSetOf(`${t}_${deps.tableSize}max`);
        if (set)
            families[`${t}_${deps.tableSize}max`] = set;
    }
    const live = state.players.filter((p) => !p.folded);
    const rank = (p) => POSTFLOP_RANK.indexOf(p.position);
    const last = live.reduce((a, b) => (rank(b) > rank(a) ? b : a), live[0]);
    const positionClass = last?.seat === seat ? 'IP' : 'OOP';
    return {
        hero: { cards, position },
        facing,
        tableType: family,
        tableSize: deps.tableSize,
        families,
        villain: { archetype: arch, rangeShape: NEUTRAL_SHAPE },
        playersInHand: live.length,
        positionClass,
        potBB: pot / bb,
        rules,
    };
};
/** The archetype's own open size, drawn from its shipped `openSizeBB` band, plus the live +1bb/limper iso
 *  convention (preflop.ts's `preflopSizing` uses the same 3bb+1/limper shape). Deterministic given `rng`. */
const openSizeFor = (arch, limpers, rng) => {
    const [lo, hi] = arch.preflop.openSizeBB;
    return lo + rng.next() * Math.max(0, hi - lo) + limpers;
};
/**
 * decideVillainPreflop - the villain's preflop intent + sizing target for the current state.
 *
 * Resolves the archetype's chart baseline, then applies its D1 knobs as chart-bounded promotes/demotes.
 * Pure given `rng`; the caller owns legalization.
 */
export const decideVillainPreflop = (state, seat, arch, cards, rng, deps) => {
    const ctx = villainCtx(state, seat, arch, cards, deps, []);
    const rec = recommendPreflop(ctx);
    const family = familyOfRangeSetId(arch.preflop.rangeSetId);
    const position = ctx.hero.position;
    const { vpipScale, limpBias, threeBetScale } = arch.preflop;
    const bb = state.spec.stakes.bb;
    const facedBB = state.currentBet / bb;
    const limpers = ctx.facing.limpers ?? 0;
    const base = rec.finalIntent;
    const out = (intent, knob, toBB) => ({
        intent,
        ...(toBB !== undefined ? { toBB } : {}),
        baselineIntent: base,
        chartKey: rec.chartKey,
        family: rec.family,
        ...(knob ? { knob } : {}),
    });
    // Sizing helpers for the aggressive intents, in the villain's own idiom.
    const openTo = () => openSizeFor(arch, limpers, rng);
    const threeBetTo = () => (ctx.positionClass === 'IP' ? 3 : 4) * facedBB + limpers;
    const fourBetTo = () => 2.3 * facedBB;
    // ── OPEN node (nobody has raised): fold / limp / open, moved by vpipScale + limpBias ────────────────
    if (ctx.facing.role === 'open') {
        if (base === 'open') {
            // TIGHTEN: a sub-1 vpip folds part of its own opening chart (a Rock's 4.8% UTG chart × 0.7 ≈ 3.4%).
            if (vpipScale < 1 && rng.next() > vpipScale)
                return out('fold', 'vpipScale');
            // LIMP: the archetype's habitual open-limp rate. This is the Whale's signature (limpBias 0.8).
            if (limpBias > 0 && rng.next() < limpBias)
                return out('limp', 'limpBias');
            return out('open', undefined, openTo());
        }
        // WIDEN: a >1 vpip plays hands its own chart folds, drawn from a shipped wider band. A loose player
        // LIMPS these rather than opening them (that is what "loose-passive" means), unless it never limps.
        if (base === 'fold' && vpipScale > 1) {
            const band = openWidenBand(deps, family, position);
            const baseChart = chart(deps, family, `${position}_RFI`);
            if (band && baseChart && inRange(band.band, cards) && !inRange(baseChart, cards)) {
                const widenMass = Math.max(0, massOf(band.band) - massOf(baseChart));
                if (rng.next() < promoteProb(vpipScale, massOf(baseChart), widenMass)) {
                    return limpBias > 0 && rng.next() < limpBias
                        ? out('limp', 'vpipScale')
                        : out('open', 'vpipScale', openTo());
                }
            }
        }
        return out('fold');
    }
    // ── DEFEND node (facing a raise): fold / call / 3bet, moved by threeBetScale + vpipScale ────────────
    if (ctx.facing.role === 'defend') {
        if (base === 'three_bet') {
            // A sub-1 3bet knob flats the thin part of its 3bet chart (promoChaser 0.4 - it has a 3bet range on
            // paper and almost never uses it). It does not FOLD them: they are the strongest hands it holds.
            if (threeBetScale < 1 && rng.next() > threeBetScale)
                return out('call', 'threeBetScale');
            return out('three_bet', undefined, threeBetTo());
        }
        if (base === 'call') {
            // A >1 3bet knob promotes flats into 3bets at the calibrated rate (firestarter 2.5× → ~7.8% vs the
            // chart's 3.1%). This is the Firecracker's signature.
            if (threeBetScale > 1) {
                const b = ctx.facing.openerPosition ? positionBucket(ctx.facing.openerPosition) : 'BTN';
                const c3 = chart(deps, family, `${position}_vs_${b}_3bet`);
                const cC = chart(deps, family, `${position}_vs_${b}_call`);
                if (c3 && cC && rng.next() < promoteProb(threeBetScale, massOf(c3), massOf(cC))) {
                    return out('three_bet', 'threeBetScale', threeBetTo());
                }
            }
            if (vpipScale < 1 && rng.next() > vpipScale)
                return out('fold', 'vpipScale');
            return out('call');
        }
        if (base === 'fold' && vpipScale > 1) {
            const b = ctx.facing.openerPosition ? positionBucket(ctx.facing.openerPosition) : 'BTN';
            const key = `${position}_vs_${b}_call`;
            const band = defendWidenBand(deps, family, key);
            const baseChart = chart(deps, family, key);
            if (band && baseChart && inRange(band, cards) && !inRange(baseChart, cards)) {
                const widenMass = Math.max(0, massOf(band) - massOf(baseChart));
                if (rng.next() < promoteProb(vpipScale, massOf(baseChart), widenMass))
                    return out('call', 'vpipScale');
            }
        }
        return out('fold');
    }
    // ── vs3bet / vs4bet / vsjam: the chart answers, with the re-raise knob on the 4bet ─────────────────
    if (base === 'four_bet') {
        if (threeBetScale < 1 && rng.next() > threeBetScale)
            return out('call', 'threeBetScale');
        return out('four_bet', undefined, fourBetTo());
    }
    if (base === 'call' && ctx.facing.role === 'vs3bet' && threeBetScale > 1) {
        const hb = positionBucket(position);
        const c4 = chart(deps, family, `${hb}_open_v3b_4bet`);
        const cC = chart(deps, family, `${hb}_open_v3b_call`);
        if (c4 && cC && rng.next() < promoteProb(threeBetScale, massOf(c4), massOf(cC))) {
            return out('four_bet', 'threeBetScale', fourBetTo());
        }
    }
    if (base === 'jam')
        return out('jam');
    return out(base);
};
//# sourceMappingURL=villainPreflop.js.map